import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import TextField from "@material-ui/core/TextField";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import CircularProgress from "@material-ui/core/CircularProgress";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import EditIcon from "@material-ui/icons/Edit";
import axios from "axios";
import envConfig from "../../bin/env.config";

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  cityInfo: {
    flex: 1,
  },
  editButton: {
    minWidth: "auto",
    padding: theme.spacing(0.5),
  },
  dialogContent: {
    minWidth: 400,
    maxWidth: 600,
  },
  searchField: {
    marginBottom: theme.spacing(2),
  },
  searchContainer: {
    display: "flex",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  searchInput: {
    flex: 1,
  },
  countrySelect: {
    minWidth: 120,
  },
  cityList: {
    maxHeight: 400,
    overflow: "auto",
  },
  cityItem: {
    cursor: "pointer",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(2),
  },
  emptyState: {
    padding: theme.spacing(2),
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
  dialogTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
}));

export default function StoreCitySelector({ store, onUpdate }) {
  const classes = useStyles();
  const token = useSelector((state) => state.session.token);
  
  const [cityData, setCityData] = useState(null);
  const [currentCityId, setCurrentCityId] = useState(store.city_id);
  const [isLoadingCity, setIsLoadingCity] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState("CA");

  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("city");
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(50);
  const [skip, setSkip] = useState(0);

  // Update currentCityId when store.city_id changes from props
  useEffect(() => {
    setCurrentCityId(store.city_id);
  }, [store.city_id]);

  // Fetch city data when currentCityId changes
  useEffect(() => {
    if (currentCityId > 0) {
      fetchCityData(currentCityId);
    } else {
      setCityData(null);
    }
  }, [currentCityId]);

  const fetchCityData = async (cityId) => {
    if (!cityId || cityId <= 0) {
      setCityData(null);
      return;
    }

    setIsLoadingCity(true);
    try {
      const url = `${envConfig.API_ROOT}${envConfig.API_VERSION}/city/get_city`;
      const result = await axios.post(
        url,
        { id: cityId },
        {
          headers: {
            Authorization: JSON.stringify(token),
          },
        }
      );

      if (result.data.success && result.data.data) {
        setCityData(result.data.data);
      } else {
        setCityData(null);
      }
    } catch (error) {
      console.error("Error fetching city data:", error);
      setCityData(null);
    } finally {
      setIsLoadingCity(false);
    }
  };

  const handleOpenDialog = () => {
    setIsDialogOpen(true);
    setSearchQuery("");
    setSearchResults([]);
    setSelectedCountry("CA");
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSearchQuery("");
    setSearchResults([]);
  };

  const handleSearchChange = (event) => {
    const query = event.target.value;
    setSearchQuery(query);
    
    if (query.trim().length >= 2) {
      searchCities(query, selectedCountry);
    } else {
      setSearchResults([]);
    }
  };

  const handleCountryChange = (event) => {
    const country = event.target.value;
    setSelectedCountry(country);
    
    if (searchQuery.trim().length >= 2) {
      searchCities(searchQuery, country);
    } else {
      setSearchResults([]);
    }
  };

  const searchCities = async (query, country = selectedCountry) => {
    setIsSearching(true);
    try {
      const url = `${envConfig.API_ROOT}${envConfig.API_VERSION}/city/search?orderBy=${orderBy}&order=${order}&limit=${limit}&skip=${skip}&country=${country}`;
      const result = await axios.post(
        url,
        { q: query },
        {
          headers: {
            Authorization: JSON.stringify(token),
          },
        }
      );

      if (result.data.success && result.data.data) {
        setSearchResults(result.data.data);
      } else {
        setSearchResults([]);
      }
    } catch (error) {
      console.error("Error searching cities:", error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSelectCity = async (city) => {
    setIsUpdating(true);
    try {
      const newCityId = city.id || city.city_id;
      const url = `${envConfig.API_ROOT}${envConfig.API_VERSION}/city/update_city_store`;
      const result = await axios.post(
        url,
        {
          store_id: store.store_id,
          city_id: newCityId,
        },
        {
          headers: {
            Authorization: JSON.stringify(token),
          },
        }
      );

      if (result.data.success) {
        // Update local state with new city data
        setCityData({
          city: city.city,
          province_id: city.province_id,
          country: city.country,
        });
        // Update currentCityId to the new value
        setCurrentCityId(newCityId);
        handleCloseDialog();
        
        // Call onUpdate callback if provided
        if (onUpdate) {
          onUpdate(newCityId);
        } else {
          // Fetch city data with the new city_id
          await fetchCityData(newCityId);
        }
      }
    } catch (error) {
      console.error("Error updating city store:", error);
      alert("Failed to update city. Please try again.");
    } finally {
      setIsUpdating(false);
    }
  };

  const renderCityDisplay = () => {
    if (isLoadingCity) {
      return <CircularProgress size={16} />;
    }

    if (currentCityId === 0 || !cityData) {
      return <Typography variant="body2">-</Typography>;
    }

    return (
      <Typography variant="body2" className={classes.cityInfo}>
        {cityData.city}
        {cityData.province_id && `, ${cityData.province_id}`}
        {cityData.country && `, ${cityData.country}`}
      </Typography>
    );
  };

  return (
    <div className={classes.container}>
      {renderCityDisplay()}
      <IconButton
        size="small"
        className={classes.editButton}
        onClick={handleOpenDialog}
        aria-label="Edit city"
      >
        <EditIcon fontSize="small" />
      </IconButton>

      <Dialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle className={classes.dialogTitle}>
          <Typography variant="h6">Select City for {store.name}</Typography>
          <Typography variant="body2">{store.address}, {store.city}, {store.province} - {store.postal}</Typography>
          
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <div className={classes.searchContainer}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search for a city..."
              value={searchQuery}
              onChange={handleSearchChange}
              className={classes.searchInput}
              autoFocus
            />
            <FormControl variant="outlined" className={classes.countrySelect}>
              <InputLabel>Country</InputLabel>
              <Select
                value={selectedCountry}
                onChange={handleCountryChange}
                label="Country"
              >
                <MenuItem value="CA">CA</MenuItem>
                <MenuItem value="US">US</MenuItem>
              </Select>
            </FormControl>
          </div>

          {isSearching ? (
            <div className={classes.loadingContainer}>
              <CircularProgress size={24} />
            </div>
          ) : searchResults.length > 0 ? (
            <List className={classes.cityList}>
              {searchResults.map((city, index) => (
                <ListItem
                  key={city.id || city.city_id || index}
                  button
                  className={classes.cityItem}
                  onClick={() => handleSelectCity(city)}
                >
                  <ListItemText
                    primary={city.city && city.city + (city.province_id ? `, ${city.province_id}` : '') + (city.country ? `, ${city.country}` : '')}
                    
                  />
                </ListItem>
              ))}
            </List>
          ) : searchQuery.trim().length >= 2 ? (
            <div className={classes.emptyState}>
              <Typography variant="body2">No cities found</Typography>
            </div>
          ) : (
            <div className={classes.emptyState}>
              <Typography variant="body2">
                Type at least 2 characters to search
              </Typography>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={isUpdating}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

